/**
 * Apartsales — `/property/$id`
 *
 * Two data paths, picked by the shape of `id`:
 *
 *   • UUID (8-4-4-4-12 hex)  → fetch from API via `listingsApi.getUnit(id)`,
 *                              adapt to web `Property` via `apiUnitToProperty`.
 *                              Loading + error UI handled with TanStack Query.
 *
 *   • Anything else          → look up in `PROPERTIES` mock (legacy ids: "1",
 *                              "sochi-marina", etc.). Pre-existing demo links
 *                              and stories keep working.
 *
 * The fork lives at this seam so the rest of the screen (PropertyDetail) stays
 * source-agnostic. Once the catalog is fully live (Phase 0 done), we drop the
 * mock branch and the UUID guard.
 *
 * AppShell hides the global TopHeader for this route (HEADERLESS_PREFIXES in
 * AppShell.tsx) — PropertyDetail renders its own floating top bar over the
 * photo carousel. TabBar stays visible.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { PropertyDetail } from '~/components/PropertyDetail';
import { PROPERTIES } from '~/data/mock';
import { listingsApi, ApiCallError } from '~/lib/api';
import { apiUnitToProperty } from '~/lib/adapters/unitToProperty';
import { VISIBILITY } from '~/lib/visibility';

type TabId = 'overview' | 'life' | 'invest' | 'reviews' | 'docs';

interface PropertySearch {
  tab?: TabId;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/property/$id')({
  validateSearch: (search: Record<string, unknown>): PropertySearch => {
    const tab = search.tab as TabId | undefined;
    if (tab === 'life' || tab === 'invest' || tab === 'reviews' || tab === 'docs') {
      return { tab };
    }
    return {};
  },
  component: PropertyScreen,
});

function PropertyScreen() {
  const { id } = Route.useParams();

  // Карточка продукта скрыта до своей очереди в дорожной карте 16 турбин.
  // Возвращаем пустое полотно — пользователь видит только TopHeader + TabBar.
  // Никаких запросов к API не уходит.
  if (!VISIBILITY.PROPERTY_DETAIL_CONTENT) {
    return <div className="min-h-full" />;
  }

  const isUuid = UUID_RE.test(id);

  // ----- Live API branch ----------------------------------------------------
  // Disabled when `id` is not a UUID — React Query won't fire and the mock
  // path takes over below.
  const {
    data: apiUnit,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['unit', id],
    queryFn: () => listingsApi.getUnit(id),
    enabled: isUuid,
    // The catalog is read-mostly and a single unit lookup is cheap to retry on
    // a hard nav, but stale-while-revalidate within a session is fine.
    staleTime: 60 * 1000,
  });

  if (isUuid) {
    if (isLoading) {
      return <PropertyLoading />;
    }
    if (isError) {
      // 404 from the API → "не найдено". Other errors → generic failure with retry hint.
      const isNotFound = error instanceof ApiCallError && error.status === 404;
      return isNotFound ? <NotFound /> : <LoadError message={messageFor(error)} />;
    }
    if (apiUnit) {
      const property = apiUnitToProperty(apiUnit);
      return <PropertyDetail property={property} />;
    }
  }

  // ----- Mock branch (legacy / demo ids) ------------------------------------
  const mockProperty = PROPERTIES.find((p) => p.id === id);
  if (!mockProperty) {
    return <NotFound />;
  }
  return <PropertyDetail property={mockProperty} />;
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function PropertyLoading() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-16 text-center">
      {/* Apple-style: just a subtle wordmark, no spinner — feels native */}
      <div className="h-10 w-10 animate-pulse rounded-full bg-white/[0.06] ring-1 ring-white/10" />
      <p className="mt-4 text-[14px] text-label-2">Загружаем объект</p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-16 text-center">
      <p className="text-[20px] font-bold text-label">ЖК не найден</p>
      <p className="mt-2 text-[14px] text-label-2">
        Возможно, объект больше не доступен.
      </p>
      <Link
        to="/"
        className="mt-6 rounded-full bg-white/10 px-5 py-2.5 text-[14px] font-semibold text-label transition-colors hover:bg-white/15 active:bg-white/20"
      >
        На главную
      </Link>
    </div>
  );
}

function LoadError({ message }: { message: string }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-16 text-center">
      <p className="text-[20px] font-bold text-label">Не удалось загрузить</p>
      <p className="mt-2 max-w-[260px] text-[14px] text-label-2">{message}</p>
      <Link
        to="/"
        className="mt-6 rounded-full bg-white/10 px-5 py-2.5 text-[14px] font-semibold text-label transition-colors hover:bg-white/15 active:bg-white/20"
      >
        На главную
      </Link>
    </div>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiCallError) {
    if (err.status === 0 || err.code === 'network_error') {
      return 'Нет связи с сервером — проверь интернет и попробуй ещё раз.';
    }
    return err.message;
  }
  return 'Что-то пошло не так. Повтори попытку через минуту.';
}
